import { BinaryBytesUnit, TimeUnit } from '../utils/convertUnits.types';

const ApiTimeUnit = ['ps', 'ns', 'µs', 'cs', 'ms', 'sec', 's', 'm', 'h'] as const;
type ApiTimeUnit = (typeof ApiTimeUnit)[number];

// 전송률(rate) 단위 — 네트워크 관례(SI 십진, 1 kbps = 1000 bps)를 따른다. byte 계열(1024)과 구분.
export const DataTransmissionUnit = [
  'bps',
  'kbps',
  'mbps',
  'gbps',
  'tbps',
  'pbps',
  'ebps',
] as const;
export type DataTransmissionUnit = (typeof DataTransmissionUnit)[number];

const ApiBinaryBiteUnit = [
  'byte',
  'bytes',
  'bytes/sec',
  'bytes/second',
  'KiB',
  'KiB/sec',
  'KiB/second',
  'MiB',
  'MiB/sec',
  'MiB/second',
  'GiB',
  'GiB/sec',
  'GiB/second',
  'TiB',
  'PiB',
  'EiB',
] as const;
type ApiBinaryBiteUnit = (typeof ApiBinaryBiteUnit)[number];

const ApiDecimalBiteUnit = [
  'byte',
  'bytes',
  'bytes/sec',
  'KB',
  'kb/sec',
  'MB',
  'mb/sec',
  'GB',
  'TB',
  'PB',
  'EB',
] as const;
type ApiDecimalBiteUnit = (typeof ApiDecimalBiteUnit)[number];

export const ApiDataUnit = [
  ...ApiBinaryBiteUnit,
  ...ApiDecimalBiteUnit,
  ...DataTransmissionUnit,
] as const;
export type ApiDataUnit = (typeof ApiDataUnit)[number];

export const ApiPercentUnit = ['%'] as const;
export type ApiPercentUnit = (typeof ApiPercentUnit)[number];

export const ApiCountUnit = [
  'Count',
  'count',
  'count/sec',
  'number',
  'block',
  'blocks',
  '°C',
  'lsn',
  'pages',
  'string',
  'table',
  'time',
  'age',
  'pps',
] as const;
export type ApiCountUnit = (typeof ApiCountUnit)[number];

export const ApiCoreUnit = [
  'nanocore',
  'microcore',
  'millicore',
  'core',
  'kilocore',
  'megacore',
  'gigacore',
  'teracore',
  'petacore',
  'exacore',
] as const;
export type ApiCoreUnit = (typeof ApiCoreUnit)[number];

export const ApiUnit = [
  ...ApiTimeUnit,
  ...ApiPercentUnit,
  ...ApiDataUnit,
  ...ApiCountUnit,
] as const;

export const convertApiDataUnitToBinaryByte = (apiUnit: ApiDataUnit): BinaryBytesUnit => {
  switch (apiUnit) {
    case 'byte':
    case 'bytes':
    case 'bytes/sec':
    case 'bytes/second':
    case 'bps':
      return 'byte';
    case 'KiB':
    case 'KiB/sec':
    case 'KiB/second':
    case 'KB':
    case 'kb/sec':
    case 'kbps':
      return 'KiB';
    case 'MiB':
    case 'MiB/sec':
    case 'MiB/second':
    case 'MB':
    case 'mb/sec':
    case 'mbps':
      return 'MiB';
    case 'GiB':
    case 'GiB/sec':
    case 'GiB/second':
    case 'GB':
    case 'gbps':
      return 'GiB';
    case 'TiB':
    case 'TB':
    case 'tbps':
      return 'TiB';
    case 'PiB':
    case 'PB':
    case 'pbps':
      return 'PiB';
    case 'EiB':
    case 'EB':
    case 'ebps':
      return 'EiB';
    default:
      throw new Error(`${apiUnit}을 ApiDataUnit에 추가해주세요.`);
  }
};

/**
 * Converts an ApiTimeUnit to a TimeUnit | 'cs'.
 *
 * @param {ApiTimeUnit} apiUnit - The unit of time from the API.
 * @returns {TimeUnit | 'cs'} The corresponding TimeUnit or 'cs' if the unit is 'cs'.
 *
 * @throws {Error} Throws an error if the provided apiUnit is not recognized.
 *
 * TimeUnit은 화면에서 보여지는 unit 리스트들입니다.
 * 'cs'는 'ms'로 바뀌어 화면에서 사용되기때문에 ReturnType을 TimeUnit | 'cs'로 설정하였습니다.
 */
export const convertTimeApiUnit = (apiUnit: ApiTimeUnit): TimeUnit | 'cs' => {
  switch (apiUnit) {
    case 'ps':
      return 'ps';
    case 'ns':
      return 'ns';
    case 'µs':
      return 'µs';
    case 'cs':
      return 'cs';
    case 'ms':
      return 'ms';
    case 'sec':
    case 's':
      return 's';
    case 'm':
      return 'm';
    case 'h':
      return 'h';
    default:
      throw new Error(`${apiUnit}을 TimeUnit에 추가해주세요.`);
  }
};

export const isApiTimeUnit = (unit: any): unit is ApiTimeUnit => {
  return ApiTimeUnit.includes(unit);
};

export const isDataTransmissionUnit = (unit: any): unit is DataTransmissionUnit => {
  return DataTransmissionUnit.includes(unit);
};

export const isApiDataUnit = (unit: any): unit is ApiDataUnit => {
  return ApiDataUnit.includes(unit);
};

export const isApiCountUnit = (unit: any): unit is ApiCountUnit => {
  return ApiCountUnit.includes(unit);
};

export const isApiCoreUnit = (unit: unknown): unit is ApiCoreUnit => {
  return ApiCoreUnit.some((coreUnit) => coreUnit === unit);
};
